import { Core } from "@faire/design-tokens";
import { optimizeImageUrlFillSquare } from "@faire/web--source/common/images/optimizeImages";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { IProductOption } from "@faire/web-api--source/indigofair/data/IProductOption";
import { IProductVariationMap } from "@faire/web-api--source/indigofair/data/IProductVariationMap";
import { observer } from "mobx-react";
import React from "react";
import styled, { css } from "styled-components";

import { SWATCH_WIDTH } from "./SpecialOptionPicker";

interface IProps {
  onVariationClick: () => void;
  variationValue: string;
  outOfStock: boolean;
  unavailable: boolean;
  selected: boolean;
  displayType: keyof typeof IProductVariationMap.IEntry.VariationDisplayType;
  hovered: boolean;
  setHoveredValue: (value?: string) => void;
  imageUrl: string | undefined;
  productOption?: IProductOption;
  isHidden: boolean;
  alt?: string;
}

const SpecialOptionVariationBase: React.FC<IProps> = (props) => {
  const isSSR = useIsSSR();

  const getOption = () => {
    if (
      props.displayType ===
      IProductVariationMap.IEntry.VariationDisplayType.SWATCH
    ) {
      return (
        <SwatchOptionWrapper
          $selected={props.selected}
          $hovered={props.hovered}
        >
          <SwatchOption
            aria-label={props.variationValue}
            $imageUrl={props.imageUrl}
            $disabled={disabled}
            role="img"
            $isSSR={isSSR}
          >
            {disabled && !props.isHidden ? <DisabledColorLine /> : null}
          </SwatchOption>
        </SwatchOptionWrapper>
      );
    }
    return (
      <ShortOption $selected={props.selected} $hovered={props.hovered}>
        <OptionTypography
          $isDisabled={disabled}
          color={disabled ? "pewter" : undefined}
        >
          {props.variationValue}
        </OptionTypography>
      </ShortOption>
    );
  };

  const disabled = (() => {
    return props.unavailable || props.outOfStock;
  })();

  return (
    <OptionWrapper
      role="radio"
      aria-checked={props.selected}
      aria-label={props.variationValue}
      onClick={props.onVariationClick}
      onMouseEnter={() => props.setHoveredValue(props.variationValue)}
      onMouseLeave={() => props.setHoveredValue()}
      aria-hidden={props.isHidden}
    >
      {getOption()}
    </OptionWrapper>
  );
};

export const SpecialOptionVariation = observer(SpecialOptionVariationBase);

/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate/Radio/Radio#Radio"
 */
const OptionWrapper = styled.button`
  outline: none;
  padding: 2px 0px;
`;

/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate/Swatch/Swatch#Swatch"
 */
const SwatchOptionWrapper = styled.div<{
  $selected: boolean;
  $hovered: boolean;
}>`
  border: 1px solid transparent;
  ${(props) =>
    props.$selected
      ? css`
          border: 1px solid ${Core.border.default};
        `
      : ""};
  ${(props) =>
    props.$hovered
      ? css`
          border: 1px solid ${Core.border.subdued};
        `
      : ""}

  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3px;
  border-radius: 50%;
`;

/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate/Swatch/Swatch#Swatch"
 */
const SwatchOption = styled.div<{
  $imageUrl?: string;
  $disabled?: boolean;
  $isSSR: boolean;
}>`
  border-radius: 50%;
  border: 1px solid ${Core.border.muted};
  width: 40px;
  height: 40px;
  ${(props) => (props.$disabled ? "opacity: 0.5" : "")};
  ${(props) =>
    props.$imageUrl
      ? css`
          background: url(${() =>
            optimizeImageUrlFillSquare(
              { url: props.$imageUrl },
              props.$isSSR,
              SWATCH_WIDTH
            )});
        `
      : ""};
  background-position: center;
  background-size: cover;
  display: flex;
  align-items: center;
  justify-content: center;
  ${responsiveMediaQueries.mobile} {
    position: relative;
  }
`;

/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate/Swatch/Swatch#Swatch"
 */
const DisabledColorLine = styled.div`
  width: 52px;
  height: 4px;
  transform: rotate(-45deg);
  position: absolute;
  z-index: 1;

  background: ${Core.border.default};
  border-top: 1px solid ${Core.border.muted};
  border-bottom: 1px solid ${Core.border.muted};
`;

const ShortOption = styled.div<{
  $selected: boolean;
  $hovered: boolean;
}>`
  border: 1px solid ${Core.border.muted};
  ${(props) =>
    props.$hovered
      ? css`
          border: 1px solid ${Core.border.subdued};
        `
      : ""};
  ${(props) =>
    props.$selected
      ? css`
          border: 1px solid ${Core.border.default};
        `
      : ""};
  min-width: 64px;
  ${responsiveMediaQueries.desktopAndAbove} {
    min-width: 56px;
  }

  padding: 20px;
  background-color: ${Core.surface.inverse};
`;

const OptionTypography = styled(Typography)<{ $isDisabled: boolean }>`
  ${(props) => (props.$isDisabled ? "text-decoration:line-through" : "")};
`;
